import React from 'react';
import { t } from '@lingui/core/macro';

import { FilterWrapper } from '@rover/react-lib/src/pages/search/SearchPage/components/Filters';
import { DayTimeAvailability } from '@rover/react-lib/src/pages/search/SearchPage/components/Filters/Dates';
import {
  CatPreferences,
  DogPreferences,
} from '@rover/react-lib/src/pages/search/SearchPage/components/Filters/Preferences';
import { shouldShowControls } from '@rover/react-lib/src/pages/search/SearchPage/utilities';
import { usePetInfoCalloutsExperiment } from '@rover/react-lib/src/pages/search/SearchPage/utilities/usePetInfoCalloutsExperiment';
import { WizardPageType } from '@rover/react-lib/src/pages/search/SearchPage/WizardPageType';
import type { FrontendConfigurationSettingsDogSizeBuckets } from '@rover/rsdk/src/apiClient/latest/model/frontendConfigurationSettingsDogSizeBuckets';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import useBrandSpecificPets from '@rover/shared/js/hooks/useBrandSpecificPets';
import PetSize from '@rover/shared/js/search/components/PetSize';
import type { SearchFiltersOptions } from '@rover/types/src/apiResponses/SearchOptionsResponse';
import type { SearchFilters } from '@rover/types/src/SearchFilters';
import type { ServiceType } from '@rover/types/src/ServiceType';
import { isCatOnlyAvailablePetTypesBasedOnPetTypes } from '@rover/utilities/getAvailablePetTypes/availablePetTypesUtils';

import { SearchInterstitialUpdateSize } from './analyticsEvents';

export type Props = {
  getServiceTypeBySlug: (arg0: string) => ServiceType | null | undefined;
  hasPets: boolean;
  isCatOnly: boolean;
  onChange: (arg0: Record<string, unknown>, arg1?: boolean) => void;
  pageType: WizardPageType;
  petSizeBuckets: FrontendConfigurationSettingsDogSizeBuckets;
  searchFilters: SearchFilters;
  serviceTypeFilterOptions: SearchFiltersOptions;
  weightUnit: string;
};

const PageTwoWizardCollection = ({
  getServiceTypeBySlug,
  hasPets,
  isCatOnly,
  onChange,
  pageType,
  petSizeBuckets,
  searchFilters,
  serviceTypeFilterOptions,
  weightUnit,
}: Props): JSX.Element => {
  const brandAvailablePets = useBrandSpecificPets();
  const isCatOnlyBrand = isCatOnlyAvailablePetTypesBasedOnPetTypes(brandAvailablePets);
  const serviceType = getServiceTypeBySlug(searchFilters.serviceType);
  const { morningAvailability, middayAvailability, eveningAvailability } = serviceTypeFilterOptions;
  const { isActive: inPetInfoCalloutsExperiment } = usePetInfoCalloutsExperiment();

  const { showPetSize, showCatPreferences, showDogPreferences, showDaytimeAvailability } =
    shouldShowControls({
      hasPets,
      isCatOnly: isCatOnly || isCatOnlyBrand,
      pageType,
      serviceOptions: serviceTypeFilterOptions,
      serviceType: serviceType || undefined,
      serviceTypeSlug: searchFilters.serviceType,
      inPetInfoCalloutsExperiment,
    });

  return (
    <>
      {pageType === WizardPageType.INLINE && showDaytimeAvailability && (
        <FilterWrapper>
          <DayTimeAvailability
            availabilityBuckets={{
              morning: morningAvailability ? morningAvailability.label : null,
              midday: middayAvailability ? middayAvailability.label : null,
              evening: eveningAvailability ? eveningAvailability.label : null,
            }}
            label={t`Which time(s) do you need?`}
            labelIsVisible
            onChange={(availability) => onChange(availability)}
            searchFilters={searchFilters}
          />
        </FilterWrapper>
      )}
      {showPetSize && (
        <FilterWrapper>
          <PetSize
            displayAsIconButtons={pageType === WizardPageType.MODAL}
            label={t`What size are your dogs (${weightUnit})?`}
            searchFilters={searchFilters}
            weightUnit={weightUnit}
            onChange={(sizeKey) => {
              emitAnalyticsEvent(
                new SearchInterstitialUpdateSize({
                  smallDogs: searchFilters.smallDogs,
                  mediumDogs: searchFilters.mediumDogs,
                  largeDogs: searchFilters.largeDogs,
                  giantDogs: searchFilters.giantDogs,
                  [sizeKey]: !searchFilters[sizeKey],
                })
              );
              onChange({ [sizeKey]: !searchFilters[sizeKey] });
            }}
            petSizeBuckets={petSizeBuckets}
          />
        </FilterWrapper>
      )}
      {showCatPreferences && (
        <FilterWrapper>
          <CatPreferences
            displayAsToggle={pageType !== WizardPageType.MODAL}
            onChange={(noCats) => onChange({ noCats })}
            value={searchFilters.noCats}
          />
        </FilterWrapper>
      )}
      {showDogPreferences && (
        <FilterWrapper>
          <DogPreferences
            displayAsToggle={pageType !== WizardPageType.MODAL}
            onChange={(personDoesNotHaveDogs) => onChange({ personDoesNotHaveDogs })}
            value={searchFilters.personDoesNotHaveDogs}
          />
        </FilterWrapper>
      )}
    </>
  );
};

export default PageTwoWizardCollection;
